import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'components/atoms/button'
import { useAuth } from 'hooks/useAuth'
import { AuthResponse } from 'models/response'
import { useStore } from 'hooks/useStore'

interface PasskeyAuthProps {
	userName?: string
	onSuccess?: (response: AuthResponse) => void
	onError?: (error: string) => void
	className?: string
	disabled?: boolean
	variant?: 'login' | 'register' | 'both'
	size?: 'sm' | 'md' | 'lg'
	registerText?: string
}

export const PasskeyAuth: React.FC<PasskeyAuthProps> = ({
	userName,
	onSuccess,
	onError,
	className = '',
	disabled = false,
	variant = 'login',
	size = 'lg',
	registerText,
}) => {
	const { loginPasskey, registerPasskey, isPasskeySupported } = useAuth()
	const { user } = useStore()
	const [isLoading, setIsLoading] = useState(false)

	// Don't render if passkeys aren't supported
	if (!isPasskeySupported()) {
		return null
	}

	const handlePasskeyLogin = async () => {
		setIsLoading(true)

		// Passkey login with discoverable credentials (no username required)
		await loginPasskey(
			(response: AuthResponse) => {
				setIsLoading(false)
				onSuccess?.(response)
			},
			(error: string) => {
				setIsLoading(false)
				onError?.(error)
			}
		)
	}

	const handlePasskeyRegister = async () => {
		setIsLoading(true)

		await registerPasskey(
			{
				userName: userName || user?.verifiedEmailAddress || user?.emailAddress || user?.userName,
				displayName: user?.name,
			},
			(response: AuthResponse) => {
				setIsLoading(false)
				onSuccess?.(response)
			},
			(error: string) => {
				setIsLoading(false)
				onError?.(error)
			}
		)
	}

	const renderLoginButton = () => (
		<Button
			variant="outline"
			fullWidth
			onClick={handlePasskeyLogin}
			disabled={disabled || isLoading}
			className={`flex items-center justify-center gap-2 ${className}`}
			size={size}
		>
			<FontAwesomeIcon
				icon={isLoading ? ['fas', 'spinner'] : ['fas', 'fingerprint']}
				className={isLoading ? 'animate-spin' : ''}
			/>
			{isLoading ? 'Authenticating...' : 'Login with Passkey'}
		</Button>
	)

	const renderRegisterButton = () => (
		<Button
			variant="primary"
			fullWidth
			onClick={handlePasskeyRegister}
			disabled={disabled || isLoading}
			className={`flex items-center justify-center gap-2 ${className}`}
			size={size}
		>
			<FontAwesomeIcon
				icon={isLoading ? ['fas', 'spinner'] : ['fas', 'plus']}
				className={isLoading ? 'animate-spin' : ''}
			/>
			{isLoading ? 'Registering...' : registerText || 'Add Passkey'}
		</Button>
	)

	if (variant === 'login') {
		return renderLoginButton()
	}

	if (variant === 'register') {
		return renderRegisterButton()
	}

	// Both variant - show both buttons
	return (
		<div className="space-y-3">
			{renderLoginButton()}
			{renderRegisterButton()}
		</div>
	)
}

export default PasskeyAuth
